// Events of sales, production and shipping [02 §5.1; 06 BR-SHP-012; 07 BR-AR-001, §10.2 P12/P16].
import { z } from 'zod';

import { businessDateSchema, idSchema } from '../kernel/index.ts';
import {
  currencyCodeSchema,
  moneySchema,
  qtyDecimalSchema,
  unitRateSchema,
} from '../units/index.ts';
import { sizeQtySchema } from '../dto/operations.ts';
import { NOTE_KIND, SHIPMENT } from '../enums/index.ts';
import { GATE_ID } from '../gates/index.ts';
import { defineEvent } from './define.ts';

const colourwaySizes = z.object({
  colourwayId: idSchema,
  sizes: z.array(sizeQtySchema).min(1),
});

export const salesOrderConfirmedV1 = defineEvent({
  type: 'sales.order.confirmed',
  version: 1,
  aggregateType: 'sales_order',
  specRefs: ['02 §5.1'],
  payload: z.object({
    salesOrderId: idSchema,
    number: z.string().min(1),
    customerId: idSchema,
    currency: currencyCodeSchema,
    revisionNo: z.int().nonnegative(),
    confirmedOn: businessDateSchema,
    /** Gates passed by override (G1) — reported in the override register [MB D4]. */
    overriddenGates: z.array(GATE_ID.schema),
    lines: z
      .array(
        z.object({
          orderLineId: idSchema,
          styleId: idSchema,
          colourwayId: idSchema,
          sizes: z.array(sizeQtySchema).min(1),
        }),
      )
      .min(1),
  }),
});

export const salesOrderRevisedV1 = defineEvent({
  type: 'sales.order.revised',
  version: 1,
  aggregateType: 'sales_order',
  specRefs: ['02 §5.1'],
  payload: z.object({
    salesOrderId: idSchema,
    number: z.string().min(1),
    revisionNo: z.int().positive(),
    reason: z.string().min(1),
    changedLineIds: z.array(idSchema),
  }),
});

export const salesOrderCancelledV1 = defineEvent({
  type: 'sales.order.cancelled',
  version: 1,
  aggregateType: 'sales_order',
  specRefs: ['02 §5.1'],
  payload: z.object({
    salesOrderId: idSchema,
    number: z.string().min(1),
    reason: z.string().min(1),
    cancelledOn: businessDateSchema,
  }),
});

export const productionCutReleasedV1 = defineEvent({
  type: 'production.cut.released',
  version: 1,
  aggregateType: 'cutting_slip',
  specRefs: ['02 §5.1'],
  payload: z.object({
    cuttingSlipId: idSchema,
    number: z.string().min(1),
    styleId: idSchema,
    releasedOn: businessDateSchema,
    orderLineIds: z.array(idSchema),
    overriddenGates: z.array(GATE_ID.schema),
    colourways: z.array(colourwaySizes).min(1),
  }),
});

/** P12 "cut complete (allocations closed)" needs a trigger; no spec table names one [assumption]. */
export const productionCutCompletedV1 = defineEvent({
  type: 'production.cut.completed',
  version: 1,
  aggregateType: 'cutting_slip',
  specRefs: ['07 §10.2 P12 [assumption: event name]'],
  payload: z.object({
    cuttingSlipId: idSchema,
    number: z.string().min(1),
    completedOn: businessDateSchema,
  }),
});

export const productionFgReceivedV1 = defineEvent({
  type: 'production.fg.received',
  version: 1,
  aggregateType: 'fg_receipt',
  specRefs: ['02 §5.1'],
  payload: z.object({
    fgReceiptId: idSchema,
    number: z.string().min(1),
    cuttingSlipId: idSchema.nullable(),
    contractorId: idSchema.nullable(),
    receivedOn: businessDateSchema,
    lines: z
      .array(
        z.object({
          skuId: idSchema,
          categoryCode: z.string().min(1),
          qty: qtyDecimalSchema,
        }),
      )
      .min(1),
  }),
});

/**
 * CR-BG2e (W2e.sewing-fg R-CORE-SEW-2, W2e.notes R-EVT-CDN-1): an allocation closed short (06 §7.4 BR-SEW-008) — the
 * outstanding pieces become a reportable contractor shortage and may raise a contractor debit note (06 §12). One name
 * (orchestrator decision); the payload is the union of both requests: the cells (colourway × size, with the finished-goods
 * inventory item when one exists), the pieces short, the contractor's price per piece (micros of `currency`, null when
 * none was set) and the reason. Amount of a debit note = shortPcs × pricePerPieceMicros.
 */
export const productionSewingClosedShortV1 = defineEvent({
  type: 'production.sewing.closed_short',
  version: 1,
  aggregateType: 'sewing_allocation',
  specRefs: ['06 §7.4 BR-SEW-008', '06 §12 CDN contractor debit note [assumption: event name]'],
  payload: z.object({
    allocationId: idSchema,
    number: z.string().min(1),
    cuttingSlipId: idSchema,
    contractorId: idSchema,
    closedOn: businessDateSchema,
    reason: z.string().min(1),
    shortPcs: z.int().positive(),
    pricePerPieceMicros: z.int().nonnegative().nullable(),
    currency: currencyCodeSchema,
    cells: z
      .array(
        z.object({
          colourwayId: idSchema,
          sizePosition: z.int().positive(),
          itemId: idSchema.nullable(),
          shortPcs: z.int().positive(),
        }),
      )
      .min(1),
  }),
});

/**
 * BR-AR-001: a shipment reaching `ready` creates the draft customer invoice. Accounting may not
 * import shipping (02 BR-ARC-006), so the draft is driven by this event [assumption: event name].
 */
export const shippingShipmentReadyV1 = defineEvent({
  type: 'shipping.shipment.ready',
  version: 1,
  aggregateType: 'shipment',
  specRefs: ['07 BR-AR-001 [assumption: event name]'],
  payload: z.object({
    shipmentId: idSchema,
    number: z.string().min(1),
    customerId: idSchema,
    currency: currencyCodeSchema,
    lines: z
      .array(
        z.object({
          shipmentLineId: idSchema,
          salesOrderLineId: idSchema.nullable(),
          skuId: idSchema,
          customerSku: z.string().nullable(),
          qty: qtyDecimalSchema,
          unitPrice: unitRateSchema,
          isSample: z.boolean(),
        }),
      )
      .min(1),
  }),
});

/** BR-SHP-012: movements, reservation consumption, status and this event commit together. */
export const shippingShipmentDispatchedV1 = defineEvent({
  type: 'shipping.shipment.dispatched',
  version: 1,
  aggregateType: 'shipment',
  specRefs: ['02 §5.1', '06 BR-SHP-012'],
  payload: z.object({
    shipmentId: idSchema,
    number: z.string().min(1),
    customerId: idSchema,
    dispatchedOn: businessDateSchema,
    movementIds: z.array(idSchema).min(1),
    overriddenGates: z.array(GATE_ID.schema),
  }),
});

/**
 * CR-BG2d (W2d.shipping R-CORE-SHP-3): a shipment cancelled before dispatch. A shipment cancelled from `ready` leaves a
 * draft customer invoice (BR-AR-001) that the consumer voids; `fromStatus` tells it whether one can exist.
 */
export const shippingShipmentCancelledV1 = defineEvent({
  type: 'shipping.shipment.cancelled',
  version: 1,
  aggregateType: 'shipment',
  specRefs: ['06 BR-SHP-013 [assumption: event name]', '07 BR-AR-001'],
  payload: z.object({
    shipmentId: idSchema,
    number: z.string().min(1),
    customerId: idSchema,
    fromStatus: SHIPMENT.states.schema,
    cancelledOn: businessDateSchema,
  }),
});

/**
 * CR-BG2d (R-CORE-SHP-3): BR-SHP-013 reopen (`dispatched → ready`, `shipping.override`): the issue was reversed by the
 * listed `shipment_reversal` movements in the same transaction; `overrideId` is the recorded override with its reason.
 */
export const shippingShipmentReopenedV1 = defineEvent({
  type: 'shipping.shipment.reopened',
  version: 1,
  aggregateType: 'shipment',
  specRefs: ['06 BR-SHP-013 [assumption: event name]'],
  payload: z.object({
    shipmentId: idSchema,
    number: z.string().min(1),
    customerId: idSchema,
    reopenedOn: businessDateSchema,
    overrideId: idSchema,
    reversalMovementIds: z.array(idSchema).min(1),
  }),
});

export const shippingNotePostedV1 = defineEvent({
  type: 'shipping.note.posted',
  version: 1,
  aggregateType: 'credit_debit_note',
  specRefs: ['02 §5.1'],
  payload: z.object({
    noteId: idSchema,
    number: z.string().min(1),
    kind: NOTE_KIND.schema,
    partnerId: idSchema,
    postedOn: businessDateSchema,
    net: moneySchema,
    tax: moneySchema,
    gross: moneySchema,
  }),
});

export const OPERATIONS_EVENTS = [
  salesOrderConfirmedV1,
  salesOrderRevisedV1,
  salesOrderCancelledV1,
  productionCutReleasedV1,
  productionCutCompletedV1,
  productionFgReceivedV1,
  productionSewingClosedShortV1,
  shippingShipmentReadyV1,
  shippingShipmentDispatchedV1,
  shippingShipmentCancelledV1,
  shippingShipmentReopenedV1,
  shippingNotePostedV1,
] as const;
